import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { clearSession, getUser } from "../lib/auth";
import { submitFeedback } from "../lib/api";

const BLUE   = "#0176CE";
const ORANGE = "#F37428";

function NavItem({ icon, label, active }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
      borderRadius: 8,
      background: active ? "rgba(1,118,206,0.08)" : "transparent",
      color: active ? BLUE : "#64748b",
      fontSize: 14, fontWeight: active ? 600 : 400,
      cursor: "pointer", marginBottom: 2,
      transition: "background 0.15s",
    }}>
      <span style={{ fontSize: 15, width: 20, textAlign: "center" }}>{icon}</span>
      {label}
    </div>
  );
}

function FeedbackModal({ onClose }) {
  const location = useLocation();
  const [text,    setText]    = useState("");
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState("");

  async function handleSubmit() {
    if (!text.trim()) return;
    setSending(true);
    setError("");
    try {
      await submitFeedback(text.trim(), window.location.href);
      setSent(true);
      setTimeout(onClose, 1800);
    } catch (e) {
      setError("Could not send feedback. Please try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.box} onClick={e => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <span style={modalStyles.title}>Share Feedback</span>
          <button onClick={onClose} style={modalStyles.closeBtn}>✕</button>
        </div>
        {sent ? (
          <div style={{ padding: "24px 0", textAlign: "center", color: "#10b981", fontSize: 14, fontWeight: 600 }}>
            Thanks — feedback received!
          </div>
        ) : (
          <>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 12px" }}>
              What's working well, what's not, or what would you like to see next?
            </p>
            <textarea
              autoFocus
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Type your feedback here…"
              rows={6}
              style={modalStyles.textarea}
            />
            {error && <p style={{ color: "#ef4444", fontSize: 12, margin: "6px 0 0" }}>{error}</p>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button onClick={onClose} style={modalStyles.cancelBtn}>Cancel</button>
              <button
                onClick={handleSubmit}
                disabled={!text.trim() || sending}
                style={{ ...modalStyles.submitBtn, opacity: (!text.trim() || sending) ? 0.5 : 1 }}
              >
                {sending ? "Sending…" : "Send Feedback"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Sidebar({ active }) {
  const navigate = useNavigate();
  const user = getUser();
  const [showFeedback, setShowFeedback] = useState(false);

  function handleLogout() { clearSession(); navigate("/login"); }

  return (
    <>
      <aside style={styles.sidebar}>
        <div style={styles.logoRow}>
          <img
            src="https://www.masterfileai.com/hubfs/logo3.png"
            alt="MasterFileAI"
            style={{ height: 30, maxWidth: 160, objectFit: "contain" }}
            onError={e => {
              e.target.style.display = "none";
              e.target.nextElementSibling.style.display = "flex";
            }}
          />
          <div style={{ display: "none", alignItems: "center", gap: 8 }}>
            <div style={styles.logoMark}>M</div>
            <span style={styles.logoText}>MasterFileAI</span>
          </div>
        </div>

        <nav style={{ flex: 1, overflowY: "auto" }}>
          <Link to="/dashboard" style={{ textDecoration: "none" }}>
            <NavItem icon="▦" label="Dashboard" active={active === "dashboard"} />
          </Link>
          <Link to="/upload" style={{ textDecoration: "none" }}>
            <NavItem icon="↑" label="Upload File" active={active === "upload"} />
          </Link>
          <Link to="/master-file" style={{ textDecoration: "none" }}>
            <NavItem icon="◈" label="Master File" active={active === "master-file"} />
          </Link>
          <Link to="/billing" style={{ textDecoration: "none" }}>
            <NavItem icon="💳" label="Billing" active={active === "billing"} />
          </Link>
          <Link to="/team" style={{ textDecoration: "none" }}>
            <NavItem icon="⚙" label="Team" active={active === "team"} />
          </Link>
        </nav>

        <div style={styles.footer}>
          <button onClick={() => setShowFeedback(true)} style={styles.feedbackBtn}>
            💬 Give Feedback
          </button>
          <div style={styles.userChip}>
            <div style={styles.avatar}>{user?.full_name?.[0]?.toUpperCase() || "U"}</div>
            <div>
              <div style={styles.userName}>{user?.full_name || "User"}</div>
              <Link to="/profile" style={{ textDecoration: "none" }}>
                <div style={{ color: BLUE, fontSize: 11, cursor: "pointer" }}>Edit profile →</div>
              </Link>
            </div>
          </div>
          <button onClick={handleLogout} style={styles.logoutBtn}>Sign out</button>
        </div>
      </aside>

      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} />}
    </>
  );
}

const styles = {
  sidebar:     {
    width: 220, background: "#fff", borderRight: "1px solid #e2e8f0",
    display: "flex", flexDirection: "column", padding: "24px 16px",
    flexShrink: 0,
    // Sticky — stays in viewport as main content scrolls
    position: "sticky", top: 0, height: "100vh", overflowY: "auto",
  },
  logoRow:     { marginBottom: 36, paddingLeft: 4, minHeight: 32, display: "flex", alignItems: "center" },
  logoMark:    { width: 32, height: 32, borderRadius: 9, background: ORANGE, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 16, flexShrink: 0 },
  logoText:    { color: "#0f172a", fontWeight: 700, fontSize: 15, letterSpacing: "-0.3px" },
  footer:      { borderTop: "1px solid #e2e8f0", paddingTop: 14, flexShrink: 0 },
  feedbackBtn: { width: "100%", marginBottom: 12, background: "rgba(1,118,206,0.06)", border: "1px solid rgba(1,118,206,0.18)", borderRadius: 7, color: BLUE, cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "7px 12px", textAlign: "left" },
  userChip:    { display: "flex", alignItems: "center", gap: 10, marginBottom: 10 },
  avatar:      { width: 32, height: 32, borderRadius: "50%", background: ORANGE, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 13, flexShrink: 0 },
  userName:    { color: "#1e293b", fontSize: 13, fontWeight: 600 },
  logoutBtn:   { background: "transparent", border: "1px solid #e2e8f0", borderRadius: 6, color: "#64748b", cursor: "pointer", fontSize: 12, padding: "6px 12px", width: "100%", textAlign: "left" },
};

const modalStyles = {
  overlay:   { position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" },
  box:       { background: "#fff", borderRadius: 12, padding: "24px", width: 440, maxWidth: "calc(100vw - 32px)", boxShadow: "0 20px 60px rgba(0,0,0,0.18)" },
  header:    { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 },
  title:     { fontSize: 16, fontWeight: 700, color: "#111827" },
  closeBtn:  { background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 16, padding: 4, lineHeight: 1 },
  textarea:  { width: "100%", padding: "10px 12px", fontSize: 13, border: "1px solid #e2e8f0", borderRadius: 8, resize: "vertical", outline: "none", color: "#374151", fontFamily: "inherit", boxSizing: "border-box" },
  cancelBtn: { background: "transparent", border: "1px solid #e2e8f0", borderRadius: 7, color: "#6b7280", cursor: "pointer", fontSize: 13, padding: "8px 16px" },
  submitBtn: { background: BLUE, border: "none", borderRadius: 7, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, padding: "8px 20px" },
};
